import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { EyeIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useAllocatePayment,
  useCancelPayment,
  useCreatePayment,
  useOutstandingInvoices,
  usePayment,
  usePaymentsPage,
  usePaymentSummary,
  useReceivablesAgeing,
} from '../../hooks/usePayments';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { PaymentInput, PaymentMode } from '../../services/payments.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const MODES: { value: PaymentMode; label: string }[] = [
  { value: 'bank_transfer', label: 'Bank transfer (NEFT / RTGS / IMPS)' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];
const modeLabel = (mode: string) => MODES.find((option) => option.value === mode)?.label.split(' (')[0] ?? mode;

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge: React.FC<{ status: 'posted' | 'cancelled' }> = ({ status }) => (
  <span className={cn(badgeClass, status === 'posted' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>
    {status === 'posted' ? 'Received' : 'Cancelled'}
  </span>
);

// The open invoices of one customer, each with an amount box. Used when a receipt is entered and
// when money on account is set against invoices later.
interface AllocationTableProps {
  customerId: string;
  available: number;
  amounts: Record<string, string>;
  onChange: (amounts: Record<string, string>) => void;
  errors: Record<string, string>;
}

const AllocationTable: React.FC<AllocationTableProps> = ({ customerId, available, amounts, onChange, errors }) => {
  const { data, isLoading } = useOutstandingInvoices({ customerId, limit: 200 }, !!customerId);
  const invoices = data?.data ?? [];
  const allocated = round2(Object.values(amounts).reduce((sum, value) => sum + (Number(value) || 0), 0));

  // Oldest due first, until the money runs out
  const autoAllocate = () => {
    let left = available;
    const next: Record<string, string> = {};
    invoices.forEach((invoice) => {
      const take = round2(Math.min(left, invoice.balance));
      if (take > 0) next[invoice.id] = String(take);
      left = round2(left - take);
    });
    onChange(next);
  };

  if (!customerId) return null;
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Set against invoices</h4>
          <p className="mt-0.5 text-xs text-gray-500">What is not set against an invoice stays on account for the customer.</p>
        </div>
        {invoices.length > 0 && available > 0 && (
          <button type="button" onClick={autoAllocate} className="self-start rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:self-auto">
            Oldest first
          </button>
        )}
      </div>
      {isLoading ? (
        <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
      ) : invoices.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">This customer has no invoice waiting for payment. The whole amount will stay on account.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className={thClass}>Invoice</th>
                <th scope="col" className={thClass}>Due</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Owed</th>
                <th scope="col" className={cn(thClass, 'w-44 text-right')}>Pay now</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</p>
                    <p className="text-xs text-gray-500">{formatDate(invoice.invoiceDate)} · {formatMoney(invoice.grandTotal)}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                    <span className={invoice.daysOverdue > 0 ? 'font-medium text-red-600' : 'text-gray-700'}>{formatDate(invoice.dueDate)}</span>
                    {invoice.daysOverdue > 0 && <span className="block text-xs text-red-600">{invoice.daysOverdue} day(s) overdue</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(invoice.balance)}</td>
                  <td className="px-4 py-2.5">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={invoice.balance}
                      value={amounts[invoice.id] ?? ''}
                      onChange={(event) => onChange({ ...amounts, [invoice.id]: event.target.value })}
                      aria-label={`Amount for ${invoice.invoiceNumber}`}
                      className={cn(inputClass, 'text-right tabular-nums', (errors[invoice.id] || Number(amounts[invoice.id]) > invoice.balance) && inputErrorClass)}
                      placeholder="0.00"
                    />
                    {Number(amounts[invoice.id]) > invoice.balance && <p className="mt-1 text-right text-xs text-red-600">More than is owed</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-600">
          Set against invoices <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(allocated)}</span>
        </span>
        <span className={cn('font-semibold tabular-nums', allocated > available ? 'text-red-600' : 'text-gray-900')}>
          {allocated > available ? `${formatMoney(round2(allocated - available))} more than is available` : `${formatMoney(round2(available - allocated))} stays on account`}
        </span>
      </div>
    </section>
  );
};

// Amounts typed against invoices, as the list the server takes
const toAllocations = (amounts: Record<string, string>) =>
  Object.entries(amounts)
    .map(([invoiceId, value]) => ({ invoiceId, amount: round2(Number(value) || 0) }))
    .filter((allocation) => allocation.amount > 0);

interface ReceiptFormProps {
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: PaymentInput) => void;
}

const ReceiptForm: React.FC<ReceiptFormProps> = ({ isPending, onCancel, onSubmit }) => {
  const { customers } = useActiveCustomers();
  const [form, setForm] = useState({ customerId: '', paymentDate: today(), amount: '', mode: 'bank_transfer' as PaymentMode, referenceNumber: '', bankName: '', notes: '' });
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const amount = Number(form.amount) || 0;
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const allocations = toAllocations(amounts);
    const allocated = round2(allocations.reduce((sum, allocation) => sum + allocation.amount, 0));
    const next: Record<string, string> = {};
    if (!form.customerId) next.customerId = 'Choose the customer who paid';
    if (!(amount > 0)) next.amount = 'Enter the amount received';
    else if (round2(amount) !== amount) next.amount = 'Use at most two decimals';
    if (!form.paymentDate) next.paymentDate = 'Enter the date';
    else if (form.paymentDate > today()) next.paymentDate = 'Not in the future';
    if (!next.amount && allocated > amount) next.allocations = 'More is set against invoices than was received';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      customerId: form.customerId,
      paymentDate: form.paymentDate,
      amount,
      mode: form.mode,
      referenceNumber: form.referenceNumber.trim() || null,
      bankName: form.bankName.trim() || null,
      notes: form.notes.trim() || null,
      allocations,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Receipt" description="Who paid, how much and how">
        <Field label="Customer" htmlFor="pay-customer" required error={errors.customerId} className="sm:col-span-2">
          <select
            id="pay-customer"
            value={form.customerId}
            onChange={(event) => {
              set('customerId')(event);
              setAmounts({});
            }}
            className={cn(inputClass, errors.customerId && inputErrorClass)}
          >
            <option value="">Select…</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
                {customer.code ? ` (${customer.code})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount received (₹)" htmlFor="pay-amount" required error={errors.amount}>
          <input id="pay-amount" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} className={cn(inputClass, 'text-right tabular-nums', errors.amount && inputErrorClass)} placeholder="0.00" />
        </Field>
        <Field label="Date received" htmlFor="pay-date" required error={errors.paymentDate}>
          <input id="pay-date" type="date" max={today()} value={form.paymentDate} onChange={set('paymentDate')} className={cn(inputClass, errors.paymentDate && inputErrorClass)} />
        </Field>
        <Field label="Received by" htmlFor="pay-mode">
          <select id="pay-mode" value={form.mode} onChange={set('mode')} className={inputClass}>
            {MODES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference" htmlFor="pay-reference" hint="Cheque number, UTR or UPI reference">
          <input id="pay-reference" type="text" maxLength={60} value={form.referenceNumber} onChange={set('referenceNumber')} className={inputClass} />
        </Field>
        <Field label="Bank" htmlFor="pay-bank">
          <input id="pay-bank" type="text" maxLength={100} value={form.bankName} onChange={set('bankName')} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="pay-notes">
          <input id="pay-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </FormCard>

      <AllocationTable customerId={form.customerId} available={amount} amounts={amounts} onChange={setAmounts} errors={{}} />
      {errors.allocations && <p className="text-sm text-red-600">{errors.allocations}</p>}

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Save Receipt'}
        </button>
      </FormActions>
    </form>
  );
};

// One receipt: what it paid, what is still on account, and the ways to change that
const ReceiptView: React.FC<{ paymentId: string; canChange: boolean; onClose: () => void }> = ({ paymentId, canChange, onClose }) => {
  const { data: payment, isLoading, isError, error } = usePayment(paymentId);
  const allocatePayment = useAllocatePayment();
  const cancelPayment = useCancelPayment();
  const [mode, setMode] = useState<'view' | 'allocate' | 'cancel'>('view');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !payment) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The receipt could not be loaded.')}</p>;

  const allocations = toAllocations(amounts);
  const wanted = round2(allocations.reduce((sum, allocation) => sum + allocation.amount, 0));
  const busy = allocatePayment.isPending || cancelPayment.isPending;

  const handleAllocate = () => {
    allocatePayment.mutate(
      { id: payment.id, allocations },
      {
        onSuccess: () => {
          toast.success(`${formatMoney(wanted)} set against invoices`);
          setAmounts({});
          setMode('view');
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The amount could not be set against the invoices')),
      },
    );
  };
  const handleCancel = () => {
    cancelPayment.mutate(
      { id: payment.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${payment.paymentNumber} cancelled`);
          setMode('view');
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The receipt could not be cancelled')),
      },
    );
  };

  return (
    <div className="space-y-5">
      <DetailCard title="Receipt" columns={4}>
        <DetailItem label="Customer">{payment.customerName || '—'}</DetailItem>
        <DetailItem label="Date received">{formatDate(payment.paymentDate)}</DetailItem>
        <DetailItem label="Received by">{modeLabel(payment.mode)}</DetailItem>
        <DetailItem label="Status"><StatusBadge status={payment.status} /></DetailItem>
        <DetailItem label="Amount">{formatMoney(payment.amount)}</DetailItem>
        <DetailItem label="Set against invoices">{formatMoney(payment.allocatedAmount)}</DetailItem>
        <DetailItem label="On account">
          <span className={payment.unallocatedAmount > 0 ? 'text-amber-700' : undefined}>{formatMoney(payment.unallocatedAmount)}</span>
        </DetailItem>
        <DetailItem label="Reference">{[payment.referenceNumber, payment.bankName].filter(Boolean).join(' · ') || '—'}</DetailItem>
        {payment.notes && (
          <DetailItem label="Notes" className="col-span-2 lg:col-span-4">
            <span className="font-normal">{payment.notes}</span>
          </DetailItem>
        )}
        {payment.status === 'cancelled' && (
          <DetailItem label="Cancelled because" className="col-span-2 lg:col-span-4">
            <span className="font-normal text-red-700">{payment.cancelReason || '—'}</span>
          </DetailItem>
        )}
      </DetailCard>

      {mode !== 'allocate' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Invoice paid</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Invoice total</th>
                <th scope="col" className={cn(thClass, 'text-right')}>From this receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(payment.allocations ?? []).length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-5 text-center text-sm text-gray-500">
                    {payment.status === 'cancelled' ? 'This receipt pays no invoice.' : 'Nothing is set against an invoice yet; the whole amount is on account.'}
                  </td>
                </tr>
              ) : (
                (payment.allocations ?? []).map((allocation) => (
                  <tr key={allocation.id}>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-medium text-gray-900">{allocation.invoiceNumber}</p>
                      <p className="text-xs text-gray-500">{formatDate(allocation.invoiceDate)}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatMoney(allocation.grandTotal)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(allocation.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {mode === 'allocate' && <AllocationTable customerId={payment.customerId} available={payment.unallocatedAmount} amounts={amounts} onChange={setAmounts} errors={{}} />}

      {mode === 'cancel' && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <p className="text-sm text-red-800">The invoices this receipt paid are owed again by what it paid. Use this for a bounced cheque or a receipt entered by mistake.</p>
          <Field label="Reason" htmlFor="pay-cancel-reason" required>
            <input id="pay-cancel-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {mode === 'view' ? (
          <>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>
              Close
            </button>
            {payment.status === 'posted' && canChange && (
              <button type="button" onClick={() => setMode('cancel')} className={secondaryButtonClass}>
                Cancel Receipt
              </button>
            )}
            {payment.status === 'posted' && canChange && payment.unallocatedAmount > 0 && (
              <button type="button" onClick={() => setMode('allocate')} className={primaryButtonClass}>
                Set Against Invoices
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={() => setMode('view')} disabled={busy} className={secondaryButtonClass}>
              Back
            </button>
            {mode === 'allocate' ? (
              <button type="button" onClick={handleAllocate} disabled={busy || wanted <= 0 || wanted > payment.unallocatedAmount} className={primaryButtonClass}>
                {allocatePayment.isPending ? 'Saving…' : 'Save'}
              </button>
            ) : (
              <button type="button" onClick={handleCancel} disabled={busy || reason.trim().length < 3} className={dangerButtonClass}>
                {cancelPayment.isPending ? 'Cancelling…' : 'Cancel Receipt'}
              </button>
            )}
          </>
        )}
      </FormActions>
    </div>
  );
};

type Tab = 'receipts' | 'outstanding' | 'ageing';

const Payments: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('gst.create');
  const canChange = can('gst.update');

  const [tab, setTab] = useState<Tab>('receipts');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'posted' | 'cancelled' | ''>('');
  const [onAccountOnly, setOnAccountOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [formOpen, setFormOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const receipts = usePaymentsPage({ page, limit, search: search || undefined, status: statusFilter, unallocated: onAccountOnly });
  const outstanding = useOutstandingInvoices({ page, limit, search: search || undefined, overdueOnly }, tab === 'outstanding');
  const ageing = useReceivablesAgeing(tab === 'ageing');
  const { data: summary } = usePaymentSummary();
  const createPayment = useCreatePayment();

  const active = tab === 'receipts' ? receipts : outstanding;
  const total = tab === 'ageing' ? 0 : active.data?.total ?? 0;
  const totalPages = tab === 'ageing' ? 1 : active.data?.totalPages ?? 1;
  const viewing = (receipts.data?.data ?? []).find((payment) => payment.id === viewingId);

  useEffect(() => {
    if (tab !== 'ageing' && active.data && page > active.data.totalPages) setPage(active.data.totalPages);
  }, [tab, active.data, page]);

  const changeTab = (next: Tab) => {
    setTab(next);
    setPage(1);
    setSearchInput('');
    setSearch('');
  };

  const handleSubmit = (input: PaymentInput) => {
    createPayment.mutate(input, {
      onSuccess: (saved) => {
        toast.success(`${saved.paymentNumber}: ${formatMoney(saved.amount)} received`);
        setFormOpen(false);
        setViewingId(saved.id);
      },
      onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The receipt could not be saved')),
    });
  };

  const stats = [
    { label: 'Received this month', value: summary ? formatMoney(summary.receivedThisMonth) : '—', hint: `${summary ? formatNumber(summary.receipts) : '—'} receipt(s) in all` },
    { label: 'Customers owe', value: summary ? formatMoney(summary.outstanding) : '—', hint: `${summary ? formatNumber(summary.openInvoices) : '—'} invoice(s) not fully paid` },
    { label: 'Overdue', value: summary ? formatMoney(summary.overdue) : '—', hint: `${summary ? formatNumber(summary.overdueInvoices) : '—'} invoice(s) past the due date` },
    { label: 'On account', value: summary ? formatMoney(summary.onAccount) : '—', hint: 'Received, not set against an invoice' },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'receipts', label: 'Receipts' },
    { key: 'outstanding', label: 'Outstanding invoices' },
    { key: 'ageing', label: 'Ageing by customer' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const ageingColumns = useMemo(
    () => [
      { key: 'notDue', label: 'Not due' },
      { key: 'days1to30', label: '1–30 days' },
      { key: 'days31to60', label: '31–60 days' },
      { key: 'days61to90', label: '61–90 days' },
      { key: 'over90', label: 'Over 90 days' },
      { key: 'total', label: 'Total owed' },
      { key: 'onAccount', label: 'On account' },
    ] as const,
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payments Received</h1>
          <p className="mt-1 text-sm text-gray-500">Money in from customers, the invoices it pays, and what is still owed.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Receive Payment
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => changeTab(item.key)}
              className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab !== 'ageing' && (
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={tab === 'receipts' ? 'Search by receipt number, customer or reference' : 'Search by invoice number or customer'}
                aria-label="Search"
                className={cn(inputClass, 'pl-9')}
              />
            </div>
            {tab === 'receipts' ? (
              <>
                <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as typeof statusFilter); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-40')}>
                  <option value="">All statuses</option>
                  <option value="posted">Received</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
                  <input type="checkbox" checked={onAccountOnly} onChange={(event) => { setOnAccountOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  With money on account
                </label>
              </>
            ) : (
              <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
                <input type="checkbox" checked={overdueOnly} onChange={(event) => { setOverdueOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Overdue only
              </label>
            )}
          </div>
        )}

        {tab === 'receipts' && (
          <div className={cn('overflow-x-auto', receipts.isFetching && receipts.data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Receipt</th>
                  <th scope="col" className={thClass}>Customer</th>
                  <th scope="col" className={thClass}>Received by</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Amount</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>On account</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {receipts.isError ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(receipts.error, 'The receipts could not be loaded.')}</td></tr>
                ) : receipts.isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (receipts.data?.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{search || statusFilter || onAccountOnly ? 'No receipt matches' : 'No payments received yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">Record money as it comes in and set it against the invoices it pays.</p>
                    </td>
                  </tr>
                ) : (
                  (receipts.data?.data ?? []).map((payment) => (
                    <tr key={payment.id} className={cn('hover:bg-gray-50', payment.status === 'cancelled' && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingId(payment.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{payment.paymentNumber}</button>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(payment.paymentDate)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{payment.customerName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {modeLabel(payment.mode)}
                        {payment.referenceNumber && <span className="block text-xs text-gray-500">{payment.referenceNumber}</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(payment.amount)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', payment.unallocatedAmount > 0 && payment.status === 'posted' ? 'font-medium text-amber-700' : 'text-gray-400')}>
                        {payment.unallocatedAmount > 0 && payment.status === 'posted' ? formatMoney(payment.unallocatedAmount) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={payment.status} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setViewingId(payment.id)} title="View" aria-label={`View ${payment.paymentNumber}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'outstanding' && (
          <div className={cn('overflow-x-auto', outstanding.isFetching && outstanding.data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Invoice</th>
                  <th scope="col" className={thClass}>Customer</th>
                  <th scope="col" className={thClass}>Due</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Invoice total</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Received</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Still owed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {outstanding.isLoading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (outstanding.data?.data ?? []).length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">{search || overdueOnly ? 'No invoice matches.' : 'No issued invoice is waiting for payment.'}</td></tr>
                ) : (
                  (outstanding.data?.data ?? []).map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(invoice.invoiceDate)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{invoice.customerName}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={invoice.daysOverdue > 0 ? 'font-medium text-red-600' : 'text-gray-700'}>{formatDate(invoice.dueDate)}</span>
                        {invoice.daysOverdue > 0 && <span className="block text-xs text-red-600">{invoice.daysOverdue} day(s) overdue</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(invoice.grandTotal)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{invoice.paidAmount > 0 ? formatMoney(invoice.paidAmount) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(invoice.balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {(outstanding.data?.total ?? 0) > 0 && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total still owed{search || overdueOnly ? ' (filtered)' : ''}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(outstanding.data?.totalBalance ?? 0)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {tab === 'ageing' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Customer</th>
                  {ageingColumns.map((column) => (
                    <th key={column.key} scope="col" className={cn(thClass, 'text-right')}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ageing.isLoading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (ageing.data?.data ?? []).length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No customer owes anything on an issued invoice.</td></tr>
                ) : (
                  (ageing.data?.data ?? []).map((row) => (
                    <tr key={row.customerId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{row.customerName || '—'}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{row.invoices} invoice(s)</p>
                      </td>
                      {ageingColumns.map((column) => (
                        <td
                          key={column.key}
                          className={cn(
                            'whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums',
                            column.key === 'total' ? 'font-semibold text-gray-900' : row[column.key] > 0 && column.key !== 'notDue' && column.key !== 'onAccount' ? 'text-red-600' : 'text-gray-700',
                          )}
                        >
                          {row[column.key] > 0 ? formatMoney(row[column.key]) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {(ageing.data?.data ?? []).length > 0 && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">Total</td>
                    {ageingColumns.map((column) => (
                      <td key={column.key} className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(ageing.data?.totals[column.key] ?? 0)}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {tab !== 'ageing' && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <span>
                Showing {firstRow}–{lastRow} of {formatNumber(total)}
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={limit}
                  onChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                  }}
                  className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
          </div>
        )}
      </div>

      <Modal isOpen={formOpen} onClose={() => !createPayment.isPending && setFormOpen(false)} title="Receive Payment" description="Record money received from a customer and set it against the invoices it pays." size="2xl">
        {formOpen && <ReceiptForm isPending={createPayment.isPending} onCancel={() => setFormOpen(false)} onSubmit={handleSubmit} />}
      </Modal>

      <Modal isOpen={!!viewingId && !formOpen} onClose={() => setViewingId(null)} title={viewing?.paymentNumber ?? 'Receipt'} size="2xl">
        {viewingId && !formOpen && <ReceiptView key={viewingId} paymentId={viewingId} canChange={canChange} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default Payments;
